import filterAssembler from './map/filter_assembler'
import mapConverters from './map/map_converters'
import GlobalOverlayManager from './global_overlay_manager'

function Filter(parsedFilter, locationFields) {

  this.modified = false;
  // The list of expressions that make up the filter
  this._data = parsedFilter;
  // 'City', 'CountyOrParish', 'PostalCode' etc.
  this._locationFields = locationFields;

}

// returns an array containing the expressions from exA that are not present in exB
Filter.expressionsRemoved = function(exA, exB) {
  return this.expressionsAdded(exB, exA);
};

// returns an array containing the expressions from exB that are not present in exA
Filter.expressionsAdded = function(exA, exB) {
  var exC = [];

  // Use some instead of forEach so we can break out of the inner loop when we
  // find a match.  That way we go through fewer inner loops.
  exB.some(expressionB => {
    var match = false;
    exA.some(expressionA => {
      try {
        if(expressionB.field === expressionA.field){
          if (expressionB.type === expressionA.type) {
            if (expressionB.type === 'shape') {
              if (expressionB.function_name === expressionA.function_name) {
                if (expressionB.function_parameters.toString() === expressionA.function_parameters.toString()) {
                  // For shape expressions, also check if operators and conjunctions match
                  // Otherwise the expression has changed.  Remove the old, add the new.
                  if (expressionB.operator === expressionA.operator && 
                      expressionB.conjunction === expressionA.conjunction) {
                    match = true;
                    return true; // Break out of the inner loop
                  }
                }
              }
            } else if (expressionB.value.sort().toString().replace(/'/g, '') === expressionA.value.sort().toString().replace(/'/g, '')) {
              // For MapOverlayShape expressions, also check if operators and conjunctions match
              if (expressionB.field === 'MapOverlayShape') {
                  // For overlayshape expressions, also check if operators and conjunctions match
                  // Otherwise the expression has changed.  Remove the old, add the new.
                  if (expressionB.operator === expressionA.operator &&
                    expressionB.conjunction === expressionA.conjunction) {
                  match = true;
                  return true; // Break out of inner loop
                }
              } else {
                // Value matches for non-MapOverlayShape expressions
                match = true;
                return true; // Break out of inner loop
              }
            }
          }
        }
      } catch(e) {
        match = false;
      }
    }); // end of inner loop
    if(!match){
      exC.push(expressionB);
    }
  }) // end of outer loop

  return exC;
};

Filter.prototype = {
  constructor: Filter,

  setData: function(newData) {
    if(newData !== this._data){
      this._data = newData;
      this.modified = true;
    }
  },

  expressions: function() {
    return this._data;
  },

  shapes: function() {
    return this._data.filter(function(element){
      return ( element.type === "shape" && element.function_name !== null );
    });
  },

  overlays: function() {
    return this._data.filter(function(element){
      return element.field === "MapOverlay";
    });
  },

  overlayShapes: function() {
    return this._data.filter(function(element) {
      return element.field === "MapOverlayShape";
    })
  },

  locations: function() {
    var self = this;
    var locationFields = this._data.filter(function(element){
      return self._locationFields.indexOf(element.field) > -1;
    });
    // Overlays and overlay shapes are location fields.
    // Include them with the return values.
    var overlays = this.overlays();
    var overlayShapes = this.overlayShapes();
    return locationFields.concat(overlays).concat(overlayShapes);
  },

  addDrawnShapeToFilter: function(expression) {
    // Hopefully this function can be avoided and we can just add the drawn shape to the filter panel directly.
    // But if we got here, the filter panel does not exist and we need to add the shape directly to the filter.
    // We need to look at the existing filter and determine:
    // - where this shape goes
    // - what operator it should use (Eq or Ne)
    // - what conjunction it should use (Or or And)

    // Get information from the filter about existing shapes
    var shapeInfo = this._analyzeShapes({includeDrawnShapes: true, includeOverlayShapes: true, includeOverlayCompatible: false});

    // Based on that information, add the new shape to the proper block group
    if (shapeInfo.hasDrawnShapes) {
      this._addDrawnShapeToDrawnShapeGroup(expression, shapeInfo.lastDrawnShapeIndex, shapeInfo.maxBlockGroup + 1);
    } else if (shapeInfo.hasOverlayShapes) {
      this._addDrawnShapeToOverlayGroup(expression, shapeInfo.lastOverlayShapeIndex, shapeInfo.maxBlockGroup + 1);
    } else {
      this._addStandaloneDrawnShapeToFilter(expression);
    }
  },

  addOverlayShapeToFilter: function(expression) {
    // Get information about existing overlay-compatible shapes
    var shapeInfo = this._analyzeShapes({includeDrawnShapes: false, includeOverlayShapes: false, includeOverlayCompatible: true});

    if (shapeInfo.hasIntersectionGroup) {
      // If there's an intersection group, add the new overlay shape to it
      var targetIndex = shapeInfo.intersectionGroupItems[shapeInfo.intersectionGroupItems.length - 1].index;
      expression.level = 1;
      expression.conjunction = 'And'; // Always use 'And' for intersection groups
      expression.conjunction_level = 1;
      expression.block_group = shapeInfo.intersectionGroupId;
      this._data.splice(targetIndex + 1, 0, expression);
    } else if (!shapeInfo.hasCompatibleShapes) {
      // There are no compatible shapes in the existing set of expressions.
      // This overlay shape should be added like this...
      expression.level = 1;
      expression.conjunction_level = 0;
      expression.conjunction = 'And';
      expression.block_group = shapeInfo.lastBlockGroupLevel + 1
      this._data.push(expression);
    } else if (shapeInfo.lastCompatibleOverlayIndex >= 0) {
      var targetIndex = shapeInfo.lastCompatibleOverlayIndex;

      if (this._data[targetIndex].level === 0) {
        // There is 1 other overlay and it is not grouped with other shapes.
        // Let's group the existing overlay with the new overlay.
        var newBlockGroup = shapeInfo.maxBlockGroup + 1;

        // Move the existing overlay into a new group
        this._data[targetIndex].level = 1;
        this._data[targetIndex].conjunction_level = 0;
        this._data[targetIndex].conjunction = "And";
        this._data[targetIndex].block_group = newBlockGroup;

        // Add the new overlay into that same group
        expression.level = 1;
        expression.conjunction = 'Or';
        expression.conjunction_level = 1;
        expression.block_group = newBlockGroup;
      } else {
        // There is a group that already exists with overlays in it.  Add this new overlay to that group.
        expression.level = 1;
        expression.conjunction = 'Or';
        expression.conjunction_level = 1;
        expression.block_group = this._data[targetIndex].block_group;
      }
      // Put the new overlay right after the last existing overlay.
      this._data.splice(targetIndex + 1, 0, expression);
    } else if (shapeInfo.firstCompatibleShapeIndex === shapeInfo.lastCompatibleShapeIndex) {
      // There are no existing overlays.  There is 1 compatible drawn shape that is not in a group.
      // Let's group the existing drawn shape with the new overlay shape.
      var newBlockGroup = shapeInfo.maxBlockGroup + 1;
      var targetIndex = shapeInfo.lastCompatibleShapeIndex;

      this._data[targetIndex].level = 1;
      this._data[targetIndex].conjunction_level = 0;
      this._data[targetIndex].conjunction = "And";
      this._data[targetIndex].block_group = newBlockGroup;
      expression.level = 1;
      expression.conjunction = 'Or';
      expression.conjunction_level = 1;
      expression.block_group = newBlockGroup;
      this._data.splice(targetIndex + 1, 0, expression);
    } else {
      var targetIndex = shapeInfo.lastCompatibleShapeIndex;
      // There are multiple compatible drawnshapes that already exist. Use their level and block_group
      // There is a group that already exists with overlays in it.  Add this new overlay to that group.
      expression.level = 1;
      expression.conjunction = 'Or';
      expression.conjunction_level = 1;
      expression.block_group = this._data[targetIndex].block_group;

      this._data.splice(targetIndex + 1, 0, expression);
    }
  },

  addPolygon: function(pointsArray, polygonName, polygonColor, desiredConjunction) {

    const pointsString = mapConverters.getStringFromPoints(pointsArray);

    let expression = {
      block_group: 0,
      level: 0,
      condition: "polygon('" + pointsString + "')",
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'polygon',
      function_parameters: [ pointsString ],
      operator: 'Eq',
      type: 'shape',
      unary_operator: null,
      value: [{
       type: 'LineString',
       coordinates: []
      }]
    }

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: polygonName,
        Color: polygonColor.replace('#','')
      }
    }

    pointsArray.forEach((latLng) => {
      expression.value[0].coordinates.push([latLng.lat(), latLng.lng()]);
    });

    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    if( window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }

    return { expression: expression, annotation: annotation};
  },

  addRectangle: function(bounds, rectangleName, rectangleColor, desiredConjunction) {

    const pointsString = mapConverters.getStringFromPoints([bounds.getNorthEast(), bounds.getSouthWest()]);

    let expression = {
      block_group: 0,
      level: 0,
      condition: "rectangle('" + parseFloat(bounds.getNorthEast().lat().toFixed(6)) + " " + parseFloat(bounds.getNorthEast().lng().toFixed(6)) + "," + parseFloat(bounds.getSouthWest().lat().toFixed(6)) + " " + parseFloat(bounds.getSouthWest().lng().toFixed(6)) +  "')",
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'rectangle',
      function_parameters: [ pointsString ],
      operator: 'Eq',
      type: 'shape',
      unary_operator: null
    }
    
    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: rectangleName,
        Color: rectangleColor.replace('#','')
      }
    }

    if(window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }

    return {expression: expression, annotation: annotation};
  },

  addRadius: function(lat, lon, radius, radiusName, radiusColor) {

    let expression = {
      block_group: 0,
      level: 0,
      condition: "radius('" + parseFloat(lat.toFixed(6)) + " " + parseFloat(lon.toFixed(6)) + "'," + parseFloat(radius.toFixed(2)) + ")",
      function_parameters: [ parseFloat(lat.toFixed(6)) + " " + parseFloat(lon.toFixed(6)), parseFloat(radius.toFixed(2)) ],
      conjunction: "And",
      conjunction_level: 0,
      field: 'Location',
      function_name: 'radius',
      operator: 'Eq',
      type: 'shape',
      unary_operator: null
    }
    this.addDrawnShapeToFilter(expression);

    this.modified = true;

    let annotation = {
      Type: 'Shape',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Label: radiusName,
        Color: radiusColor.replace('#','')
      }
    }

    if(window.nativeAppInterface && typeof window.nativeAppInterface.addShape === 'function' && window.nativeAppInterface.isNative()) {
      const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

      window.nativeAppInterface.addShape({
        quickSearchId,
        savedSearchId,
        comparableSearchId,
        shape: expression,
        annotation
      });
    }
    return {expression: expression, annotation: annotation};
  },

  toString: function(){
    return filterAssembler(this._data);
  },

  removeShapeByCondition: function(condition) {
    for (var x = 0; x < this._data.length; x++) {
      if (this._data[x].condition == condition) {
        // Check if this is a MapOverlayShape and if there are other MapOverlayShapes
        if (this._data[x].field === 'MapOverlayShape') {
          // Count remaining MapOverlayShapes after removal
          let remainingMapOverlayShapes = this._data.filter((item, index) =>
            index !== x && item.field === 'MapOverlayShape'
          );

          // If there are remaining MapOverlayShapes, we need to adjust conjunctions
          if (remainingMapOverlayShapes.length > 0) {
            // Find the first remaining MapOverlayShape and set its conjunction to 'And'
            // This ensures proper logical structure when removing from an 'Or' group
            for (let y = 0; y < this._data.length; y++) {
              if (y !== x && this._data[y].field === 'MapOverlayShape') {
                this._data[y].conjunction = 'And';
                break;
              }
            }
          }
        }

        this.removeShape(this._data[x]);
        return;
      }
    }
  },

  removeShape: function(element) {
    var index = this._data.indexOf(element);
    if (index >= 0) {
      this._data.splice(index, 1);
      this.modified = true;

      if(window.nativeAppInterface && typeof window.nativeAppInterface.removeShape === 'function' && window.nativeAppInterface.isNative()) {
        const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};
        window.nativeAppInterface.removeShape({
          quickSearchId,
          savedSearchId,
          comparableSearchId,
          shapeId: element.condition,
        });
      }
    }

    if (this.modified) {
      for (var x = 0; x < this._data.length; x++) {
        if (this._data[x].type == 'shape' || this._data[x].field === 'MapOverlayShape') {
          // The first drawn shape or MapOverlayShape should have a conjunction of And.
          // If the removed item was the first drawn shape or MapOverlayShape, we need to make sure
          // that the new first drawn shape or MapOverlayShape has the right conjunction.
          this._data[x].conjunction = 'And';
          // If this shape is in a grouped block (level 1), it should also have conjunction_level 0
          // to properly anchor the group to the rest of the filter
          if (this._data[x].level === 1) {
            this._data[x].conjunction_level = 0;
          }
          break;
        }
      }
    }
  },

  removeShapes: function() {
    var self = this;

    var shapeRemoved = false;

    this._data.forEach(function(element, index, dataArray){
      if(element.type === 'shape'){
        dataArray.splice(index, 1);
        self.modified = true;
        shapeRemoved = true;

         if(window.nativeAppInterface && typeof window.nativeAppInterface.removeShape === 'function' && window.nativeAppInterface.isNative()) {
          const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};
          window.nativeAppInterface.removeShape({
            quickSearchId,
            savedSearchId,
            comparableSearchId,
            shapeId: element.condition,
          });
        }
      }
    });

  },

  // Now that we can edit shapes directly, we need a way to replace
  // the values in the associated expression instead of deleting the
  // old and inserting a new one.
  replaceShape: function(oldCondition, newCondition, newFunctionParams) {
    for (var x = 0; x < this._data.length; x++) {
      if (this._data[x].condition == oldCondition) {
        this._data[x].condition = newCondition;
        this._data[x].function_parameters = newFunctionParams;
        this.modified = true;

        if(window.nativeAppInterface && typeof window.nativeAppInterface.replaceShape === 'function' && window.nativeAppInterface.isNative()) {
          const { quickSearchId, savedSearchId, comparableSearchId } = window.searchResultsData || {};

          window.nativeAppInterface.replaceShape({
            quickSearchId,
            savedSearchId,
            comparableSearchId,
            shapeId: oldCondition,
            shape: this._data[x]
          });
        }
        return;
      }
    }
  },

  addMapOverlayShapeToFilter: function(overlayShape, desiredConjunction) {
    let expression = {
      type: 'character',
      block_group: 0,
      condition: "'" + overlayShape.Id + "'",
      conjunction: "And",
      conjunction_level: 0,
      custom_field: false,
      field: 'MapOverlayShape',
      level: 0,
      operator: 'Eq',
      value: [overlayShape.Id]
    }

    let annotation = {
      Type: 'Overlay',
      Match: {
        Substring: expression.condition
      },
      Attributes: {
        Shape: overlayShape.Id,
        Overlay: overlayShape.OverlayId,
        OverlayName: overlayShape.OverlayName,
        ShapeName: overlayShape.Name,
        Color: overlayShape.Color.replace('#','')
      }
    }

    this.addOverlayShapeToFilter(expression, desiredConjunction)
    this.modified = true;
    return { expression: expression, annotation: annotation};
  },


  removeMapOverlayShape: function(id) {
    let modified = false;
    this._data.forEach(function(element, index, dataArray){
      if (element.condition.includes(id)) {
        dataArray.splice(index, 1)
        modified = true;
      }
    });

    if (modified) {
      this.modified = true;
      for (var x = 0; x < this._data.length; x++) {
        if (this._data[x].field == 'MapOverlayShape') {
          // The first shape should have a conjunction of And.
          // If the first shape was removed, we need to make sure
          // that the new first shape has the right conjunction.
          this._data[x].conjunction = 'And';
          break;
        }
      }
    }

  },

  propertyType: function() {
    var propertyTypes = [];

    this._data.forEach(function(element, index, dataArray){
      if(element.field === 'PropertyType'){
        element.value.forEach((value) => {
          propertyTypes.push(value.replace(/'/g, ''));
        })
      }
    });

    return propertyTypes;
  },

  propertyClass: function() {
    var propertyClasses = [];

    this._data.forEach(function(element, index, dataArray){
      if(element.field === 'PropertyClass'){
        element.value.forEach((value) => {
          propertyClasses.push(value.replace(/'/g, ''));
        })
      }
    });

    return propertyClasses;
  },


  equalToOriginal: function(ignoreRemovedDataSource = false) {
    let removedExpressions = Filter.expressionsRemoved(mapSupport.parsedFilter, this._data)
    let addedExpressions = Filter.expressionsAdded(mapSupport.parsedFilter, this._data)
    let equal = true
    if (removedExpressions.length > 0) {
      if (removedExpressions.length > 1 || !ignoreRemovedDataSource || removedExpressions[0].field !== 'MlsId') {
        equal = false;
      }
    }
    if (addedExpressions.length > 0) {
      equal = false;
    }
    return equal;
  },

  // Unified method to analyze shapes based on different criteria
  _analyzeShapes: function(options) {
    options = options || {};
    var includeDrawnShapes = options.includeDrawnShapes !== false; // default true
    var includeOverlayShapes = options.includeOverlayShapes !== false; // default true
    var includeOverlayCompatible = options.includeOverlayCompatible || false;
    var shapesOperator = includeOverlayCompatible ? this.drawnShapesOperator() : null;

    var result = {
      hasDrawnShapes: false,
      hasOverlayShapes: false,
      hasCompatibleShapes: false,
      lastDrawnShapeIndex: -1,
      lastOverlayShapeIndex: -1,
      firstCompatibleShapeIndex: -1,
      lastCompatibleShapeIndex: -1,
      lastCompatibleOverlayIndex: -1,
      maxBlockGroup: 0,
      lastBlockGroup: 0,
      lastBlockGroupLevel: 0,
      hasIntersectionGroup: false,
      intersectionGroupId: -1,
      intersectionGroupItems: []
    };

    var data = this._data;
    var length = data.length;

    // First pass: Group items by block_group and collect basic info
    const blockGroups = {};
    for (var i = 0; i < length; i++) {
      var item = data[i];
      var blockGroup = item.block_group || 0;

      if (blockGroup > result.maxBlockGroup) {
        result.maxBlockGroup = blockGroup;
      }

      // Initialize block group if not exists
      if (!blockGroups[blockGroup]) {
        blockGroups[blockGroup] = [];
      }
      blockGroups[blockGroup].push({...item, index: i});

      // Check for drawn shapes
      if (includeDrawnShapes && item.type === 'shape') {
        result.hasDrawnShapes = true;
        result.lastDrawnShapeIndex = i;
      }

      // Check for overlay shapes
      if (includeOverlayShapes && item.field === 'MapOverlayShape') {
        result.hasOverlayShapes = true;
        result.lastOverlayShapeIndex = i;
      }
    }

    // Second pass: Analyze block groups for compatibility
    if (includeOverlayCompatible) {
      for (const [blockGroupId, groupItems] of Object.entries(blockGroups)) {
        const isGroupCompatible = this._isBlockGroupCompatible(groupItems);

        if (isGroupCompatible) {
          result.hasCompatibleShapes = true;
          if (result.firstCompatibleShapeIndex === -1) {
            result.firstCompatibleShapeIndex = groupItems[0].index;
          }
          result.lastCompatibleShapeIndex = groupItems[groupItems.length - 1].index;

          // Track the last compatible overlay specifically
          const lastOverlayInGroup = groupItems.filter(item => item.field === 'MapOverlayShape').pop();
          if (lastOverlayInGroup) {
            result.lastCompatibleOverlayIndex = lastOverlayInGroup.index;
          }

          // Check if this is an intersection group
          if (this._isIntersectionGroup(groupItems)) {
            result.hasIntersectionGroup = true;
            result.intersectionGroupId = parseInt(blockGroupId);
            result.intersectionGroupItems = groupItems;
          }
        }
      }
    }

    // Get information about the last block group and its level
    if (length > 0) {
      var lastExpression = data[length - 1];
      result.lastBlockGroup = lastExpression.block_group || 0;
      result.lastBlockGroupLevel = lastExpression.level || 0;
    }

    return result;
  },

  _isBlockGroupCompatible: function(groupItems) {
    // A block group is compatible if:
    // 1. All items are location-based (drawn shapes or MapOverlayShapes)
    // 2. All items have operator 'Eq'
    // This is only used for overlay shapes.  That is why the operator has to be 'Eq'.

    return groupItems.every(item => {
      const isLocationBased = (item.type === 'shape' && item.field === 'Location') ||
                             (item.field === 'MapOverlayShape');
      const hasEqOperator = item.operator === 'Eq';
      return isLocationBased && hasEqOperator;
    });
  },

  _isIntersectionGroup: function(groupItems) {
    // A group is an intersection group if:
    // 1. It has more than 1 shape (drawn or overlay)
    // 2. All shapes use 'And' conjunctions to connect them

    if (groupItems.length <= 1) {
      return false;
    }

    // Check if all shapes use 'And' conjunction
    return groupItems.every(item => item.conjunction === 'And');
  },

  // This method is used to determine the initial operator for
  // drawn shapes (locations). The operator is determined by analyzing
  // the filter expressions block groups and the conjunctions and operators
  // used by MapOverlayShapes and Locations. (We need to include MapOverlayShapes
  // because they can be in the same block group as Locations.)
  drawnShapesOperator: function() {
    // Find Location field expressions
    const locationExpressions = this._data.filter(expr => expr.field === 'Location');
    // Find MapOverlayShape field expressions
    const mapOverlayShapeExpressions = this._data.filter(expr => expr.field === 'MapOverlayShape');

    // Determine the proper operator based on expression analysis
    return this.determineDrawnShapesOperator(locationExpressions, mapOverlayShapeExpressions);
  },

  // Private helper method to determine the drawn shapes operator
  determineDrawnShapesOperator: function(locationExpressions, mapOverlayShapeExpressions) {
    // Collect all expressions from both fields
    const allExpressions = [...locationExpressions, ...mapOverlayShapeExpressions];

    if (allExpressions.length === 0) {
      return 'or';
    }

    // Group expressions by block_group
    const expressionsByBlock = {};
    allExpressions.forEach(expr => {
      const blockGroup = expr.block_group || 0;
      if (!expressionsByBlock[blockGroup]) {
        expressionsByBlock[blockGroup] = [];
      }
      expressionsByBlock[blockGroup].push(expr);
    });

    // Analyze each block group
    for (const blockGroup in expressionsByBlock) {
      const expressions = expressionsByBlock[blockGroup];
      const blockGroupNum = parseInt(blockGroup);

      // For block_group > 0, skip the first conjunction as it's external
      if (blockGroupNum > 0 && expressions.length > 1) {
        // Get conjunctions starting from the second expression (internal conjunctions)
        const internalConjunctions = expressions.slice(1).map(expr => expr.conjunction);
        const uniqueInternalConjunctions = [...new Set(internalConjunctions)];
        const operators = expressions.map(expr => expr.operator);
        const uniqueOperators = [...new Set(operators)];

        // Rule 1: If individual comparisons use Eq and are joined by Or
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Eq' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'Or') {
          return 'or';
        }

        // Rule 2: If individual comparisons use Ne (should all be joined by And)
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Ne' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'And') {
          return 'not';
        }

        // Rule 3: If individual comparisons use Eq and are joined by And
        if (uniqueOperators.length === 1 && uniqueOperators[0] === 'Eq' &&
            uniqueInternalConjunctions.length === 1 && uniqueInternalConjunctions[0] === 'And') {
          return 'and';
        }
      } else {
        // For block_group 0 or single expressions - only concerned with shapes
        // In block group 0, shapes will always use And conjunction, so ignore conjunctions
        // Only examine Location field comparisons
        const locationExpressionsInBlock = expressions.filter(expr => expr.field === 'Location');

        // There should be 0 or 1 location expression in block 0
        if (locationExpressionsInBlock.length === 0) {
          return 'or';
        } else {
          // There should only be 1 location expression in this block
          // Only use the first one to determine the operator
          const operator = locationExpressionsInBlock[0].operator;

          // Simple rule: If comparison is Eq, operator is or. If comparison is Ne, operator is not.
          if (operator === 'Eq') {
            return 'or';
          } else if (operator === 'Ne') {
            return 'not';
          }
        }
        // If there are more than 1 location expressions, ignore them
      }
    }

    // Default fallback
    return 'or';
  },

  _addStandaloneDrawnShapeToFilter: function(expression) {
    expression.level = 0;
    expression.block_group = 0;
    expression.conjunction = 'And';
    this._data.push(expression);
  },

  _addDrawnShapeToOverlayGroup: function(expression, targetIndex, newBlockGroup) {
    // No drawn shapes.  But there are overlays.
    // OVERLAYS ARE CURRENTLY ALWAYS GROUPED TOGETHER WITH AN 'OR' CONJUNCTION.
    if (this._data[targetIndex].block_group == 0) {
      // Group the only overlay with the only (and new) drawn shape
      expression.level = 1;
      this._data[targetIndex].level = 1;
      expression.block_group = newBlockGroup;
      this._data[targetIndex].block_group = newBlockGroup;
      expression.conjunction = 'Or'; // No shapes.  The first one is an 'Or'
      expression.operator = 'Eq'
      this._data.splice(targetIndex + 1,0, expression);
    } else {
      // Add the only new drawn shape to the same group as the overlays (currently always in the same group and always 'Or)
      expression.level = this._data[targetIndex].level;
      expression.block_group = this._data[targetIndex].block_group;
      expression.conjunction = 'Or'; // No shapes.  The first one is an 'Or'
      expression.operator = 'Eq';
      this._data.splice(targetIndex + 1,0, expression);
    }
  },

  _addDrawnShapeToDrawnShapeGroup: function(expression, targetIndex, newBlockGroup) {
    let lastDrawnShapeExpression = this._data[targetIndex];
    // We have drawn shapes.  This should be in the same group as the other drawn shapes.
    if (lastDrawnShapeExpression.block_group == 0) {
      //  We have a single drawn shape at the top level of the filter.  We need to group it with the new shape
      lastDrawnShapeExpression.level = 1;
      expression.level = 1;

      lastDrawnShapeExpression.block_group = newBlockGroup;
      expression.block_group = newBlockGroup;
      
      lastDrawnShapeExpression.conjunction_level = 0;
      expression.conjunction_level = 1;

      lastDrawnShapeExpression.conjunction = 'And'; // TO connect all shapes to the rest of the filter
      expression.operator = lastDrawnShapeExpression.operator;
      if (lastDrawnShapeExpression.operator === 'Eq') {
        expression.conjunction = 'Or'; // No way to tell if it is Within or Intersection.  Default to Or.
      } else {
        expression.conjunction = 'And'; // If Ne, always join with And.
      }

      this._data.splice(targetIndex + 1,0, expression);
    } else {
      // Add this new shape to the group of existing shapes.  Use the desired conjunction if possible.
      expression.block_group = lastDrawnShapeExpression.block_group;

      expression.level = 1;
      expression.conjunction_level = 1;

      if (lastDrawnShapeExpression.conjunction_level === 0) {
        // Check if this is an intersection group (all shapes use 'And' conjunctions)
        var isIntersectionGroup = this._isIntersectionGroup(this._data.filter(item => item.block_group === lastDrawnShapeExpression.block_group));

        if (isIntersectionGroup) {
          expression.conjunction = 'And'; // Use 'And' for intersection groups
        } else if (lastDrawnShapeExpression.operator === 'Eq') {
          expression.conjunction = 'Or'; // Default to Or for union groups
        } else {
          expression.conjunction = 'And'; // If Ne, always join with And.
        }
      } else {
        expression.conjunction = lastDrawnShapeExpression.conjunction;
      }
      expression.operator = lastDrawnShapeExpression.operator;
      this._data.splice(targetIndex + 1,0, expression);
    }
  }
};

export default Filter;
